import { useCallback, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as userActions from "../../../../redux/actions/user";
import { orderMethods, orderTypeIds } from "../../../../shared/constants/order";
import { shippingMethods } from "../../../../shared/constants/store";
import * as orderRepo from "../../../../shared/repos/graphql/order";
import { getDeliveryAddress } from "../utils/addressStorage";
import ddLog from "../../../../shared/utilities/datadog/log";

/**
 * @typedef {Object} MutationParams
 * @property {() => void} [onSuccess]
 * @property {(error: Error) => void} [onError]
 */

/**
 * @typedef {Object} DeliveryConfirmation
 * @property {boolean} pending
 * @property {Error | null} error
 * @property {(params: MutationParams) => Promise<void>} mutate
 *
 * @returns {DeliveryConfirmation}
 */
export default function useDeliveryConfirmation() {
  const dispatch = useDispatch();
  const userCartId = useSelector(state => state.user.userCartId);

  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      error: null,
      pending: false
    }
  );

  const handleConfirmDelivery = useCallback(
    /**
     * @param {MutationParams} [params]
     * @returns {Promise<void>}
     */
    async (params = {}) => {
      if (!userCartId) return;

      setState({ pending: true });

      try {
        // Retrieve the saved delivery address from local storage
        const savedAddress = getDeliveryAddress(userCartId);

        if (!savedAddress) {
          setState({ pending: false });
          return;
        }

        // Warn if lat/lng are missing from saved address — send structured info to Datadog
        if (!savedAddress.lat || !savedAddress.lng) {
          ddLog.logInfo('Saved delivery address is missing lat/lng coordinates', savedAddress); 
        }

        const data = {
          storeId: null,
          orderTypeId: orderTypeIds.delivery,
          shippingMethod: shippingMethods.delivery,
          address: {
            address1: savedAddress.address1,
            city: savedAddress.city,
            state: savedAddress.state,
            postcode: savedAddress.postcode,
            lat: savedAddress.lat,
            lng: savedAddress.lng,
            countryId: savedAddress.countryId
          }
        };

        await orderRepo.updateOrderLocation(userCartId, data);
        await orderRepo.getOrder(userCartId);
        dispatch(userActions.setUserOrderMethod(orderMethods.delivery));
        dispatch(userActions.setUserAddress(savedAddress));

        // eslint-disable-next-line no-unused-expressions
        params.onSuccess?.();
      } catch (error) {
        setState({ error });
        // eslint-disable-next-line no-unused-expressions
        params.onError?.(error);
      } finally {
        setState({ pending: false });
      }
    },
    [dispatch, userCartId]
  );

  return {
    ...state,
    mutate: handleConfirmDelivery
  };
}
